import {computed, onMounted, reactive, ref} from "vue";
import {useCookies} from "@vueuse/integrations/useCookies";
import {getDeferredPrompt, clearDeferredPrompt} from "@/Plugins/InstallPromptCapture.js";

export function useInstall() {
    const cookies = useCookies(["show-install"]);
    const deferredInstallPrompt = ref<BeforeInstallPromptEvent | null>(getDeferredPrompt());

    const isInStandaloneMode = computed(() => {
        return (
            window.matchMedia("(display-mode: standalone)").matches ||
            ("standalone" in window.navigator && (window.navigator as any).standalone) ||
            document.referrer.includes("android-app://")
        );
    });

    const isIos = computed(() => {
        return /ipad|iphone|ipod/.test(navigator.userAgent.toLowerCase()) && !(window as any).MSStream;
    });

    const isMac = computed(() => {
        return /(macintosh|macintel|macppc|mac68k|macos|mac os x)/i.test(window.navigator.userAgent.toLowerCase());
    });

    const isSafari = computed(() => {
        const ua = window.navigator.userAgent.toLowerCase();
        return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
    });

    const show = reactive({
        install: false,
        iosInstall: false,
    });

    const setShow = (key: keyof typeof show, value: boolean) => {
        show[key] = value;
    };

    const showInstallPrompt = () => {
        if (isIos.value && !isInStandaloneMode.value) {
            setShow("iosInstall", true);
        } else {
            if (!deferredInstallPrompt.value) {
                console.error('ERROR: No deferred prompt available!');
                return;
            }

            try {
                deferredInstallPrompt.value.prompt();
                deferredInstallPrompt.value.userChoice.then((choiceResult) => {
                    if (choiceResult.outcome === "accepted") {
                        console.log('[useInstall] User ACCEPTED install');
                    } else {
                        console.log('[useInstall] User DISMISSED install');
                    }
                    dismissInstallPrompt();
                }).catch((error) => {
                    console.error('[useInstall] userChoice promise rejected:', error);
                });
            } catch (error) {
                console.error('[useInstall] Error calling prompt():', error);
            }
        }
    };

    const dismissInstallPrompt = () => {
        cookies.set("show-install", "false", {expires: new Date(new Date().setFullYear(new Date().getFullYear() + 1))});
        setShow("install", false);
        setShow("iosInstall", false);
        clearDeferredPrompt();
    };

    // Initialize install prompt logic
    if (!isInStandaloneMode.value) {
        if (isIos.value && cookies.get("show-install")?.toString() !== "false") {
            setShow("install", true);
        }
        // Mac support - show button immediately for Chrome/Edge, but NOT Safari
        if (isMac.value && !isSafari.value && cookies.get("show-install")?.toString() !== "false") {
            setShow("install", true);
        }
    }

    onMounted(() => {
        window.addEventListener('app-install-prompt-ready', ((e: CustomEvent) => {
            deferredInstallPrompt.value = e.detail.prompt;
            if (cookies.get("show-install")?.toString() !== "false") {
                setShow("install", true);
            }
        }) as EventListener);

        if (deferredInstallPrompt.value && cookies.get("show-install")?.toString() !== "false") {
            setShow("install", true);
        }
    });

    // Check if install is available
    const isInstallAvailable = computed(() => {
        // For Mac: only available if NOT Safari (Chrome/Edge support beforeinstallprompt)
        // For iOS: always available (we show manual instructions)
        // For others: available if deferred prompt exists
        return !isInStandaloneMode.value && (
            isIos.value ||
            (isMac.value && !isSafari.value) ||
            !!deferredInstallPrompt.value
        );
    });

    return {
        isInStandaloneMode,
        isIos,
        isMac,
        show,
        showInstallPrompt,
        dismissInstallPrompt,
        isInstallAvailable,
        deferredInstallPrompt,
    };
}

// Type for BeforeInstallPromptEvent
interface BeforeInstallPromptEvent extends Event {
    prompt: () => void;
    userChoice: Promise<{outcome: "accepted" | "dismissed"}>;
}
